import { useState, type KeyboardEvent } from 'react'
import type { Language } from '../i18n.js'
import type { Theme } from '../theme.js'
import { nextLanguage } from '../language.js'
import { useT } from './i18n-context.js'

/**
 * Меню шапки (D10).
 *
 * В шапке одна кнопка. До этого их было пять — вход, кампании,
 * «о приложении», язык, тема, — и на телефоне 390 пикселей заголовок
 * от них съезжал в две строки, а имя персонажа уходило под кнопки.
 * Считать кнопки — не самоцель: беда была в том, что половину из них
 * человек каждый раз искал глазами заново, потому что набор в шапке
 * менялся от экрана к экрану.
 *
 * Меню закрывается тремя способами: выбором пункта, нажатием мимо
 * и Escape. Нажатие мимо ловит подложка, а не слушатель на документе:
 * подложка живёт и умирает вместе с меню, а слушатель на документе
 * переживает компонент ровно настолько, насколько ошибётся уборка.
 *
 * Тема и язык стоят здесь же, но не пунктами, а переключателями:
 * у них два положения, и пункт меню «Тема» пришлось бы открывать
 * во второе меню ради выбора из двух.
 */

export type AppMenuProps = {
  /** Вошёл аккаунтом (D8): у него есть главная и выход. */
  readonly signedIn: boolean
  /** Адрес аккаунта: за столом один ноутбук на всех, и чей он — важно. */
  readonly email: string | null
  readonly theme: Theme
  readonly language: Language
  readonly onHome: () => void
  /**
   * Свои записи контента (D53). В меню, а не только кнопкой внизу
   * главной: редактор написан для мастера, а мастер заходит в него
   * с экрана кампании, а не с главной, — и кнопки, до которой надо
   * прокрутить два списка, он не находит.
   */
  readonly onContent: () => void
  readonly onAbout: () => void
  /** «Что нового»: место, куда можно заглянуть, без уведомлений. */
  readonly onWhatsNew: () => void
  readonly onSignIn: () => void
  readonly onSignOut: () => void
  readonly onTheme: (theme: Theme) => void
  readonly onLanguage: (language: Language) => void
}

export function AppMenu({
  signedIn,
  email,
  theme,
  language,
  onHome,
  onContent,
  onAbout,
  onWhatsNew,
  onSignIn,
  onSignOut,
  onTheme,
  onLanguage,
}: AppMenuProps) {
  const t = useT()
  const [open, setOpen] = useState(false)

  const close = () => {
    setOpen(false)
  }

  /** Пункт меню: сделал — закрылось. Меню открыто ради одного шага. */
  const item = (label: string, action: () => void, testId: string) => (
    <button
      type="button"
      className="app-menu-item"
      data-testid={testId}
      onClick={() => {
        close()
        action()
      }}
    >
      {label}
    </button>
  )

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') close()
  }

  const targetLanguage = nextLanguage(language)
  const targetTheme: Theme = theme === 'dark' ? 'light' : 'dark'

  return (
    <div className="app-menu" onKeyDown={onKeyDown}>
      <button
        type="button"
        className="app-icon-button app-menu-button"
        aria-expanded={open}
        aria-label={t('menu.title')}
        title={t('menu.title')}
        data-testid="menu-button"
        onClick={() => {
          setOpen(!open)
        }}
      >
        <span aria-hidden="true">☰</span>
      </button>

      {open && (
        <>
          {/* Нажатие мимо меню закрывает его. Кнопка, а не div:
              то же самое делает клавиатура, и подпись у неё своя. */}
          <button
            type="button"
            className="app-menu-backdrop"
            aria-label={t('menu.close')}
            onClick={close}
          />
          {/* Не `role="menu"`: у настоящего меню ARIA требует
              навигации стрелками и своего управления фокусом,
              а здесь список обычных кнопок, и притворяться меню
              значит обещать программе чтения с экрана поведение,
              которого нет. */}
          <div
            className="app-menu-panel"
            aria-label={t('menu.title')}
            data-testid="menu-panel"
          >
            {signedIn && item(t('menu.home'), onHome, 'menu-home')}
            {/* Свои записи заводит и игрок без аккаунта (D62): они
                привязаны к устройству и работают за столом после
                подтверждения мастером. */}
            {item(t('custom.title'), onContent, 'menu-content')}
            {item(t('whatsNew.title'), onWhatsNew, 'menu-whats-new')}
            {item(t('about.title'), onAbout, 'menu-about')}

            <div className="app-menu-row">
              <span className="app-menu-label">{t('theme.title')}</span>
              <button
                type="button"
                className="app-button app-button-quiet"
                data-testid="menu-theme"
                onClick={() => {
                  onTheme(targetTheme)
                }}
              >
                {t(`theme.${targetTheme}`)}
              </button>
            </div>

            <div className="app-menu-row">
              <span className="app-menu-label">{t('language.title')}</span>
              <button
                type="button"
                className="app-button app-button-quiet"
                data-testid="menu-language"
                onClick={() => {
                  onLanguage(targetLanguage)
                }}
              >
                {t(`language.${targetLanguage}`)}
              </button>
            </div>

            {signedIn ? (
              <>
                {email !== null && (
                  <p className="app-menu-note" data-testid="menu-account">
                    {t('auth.signedInAs', { email })}
                  </p>
                )}
                {item(t('auth.signOut'), onSignOut, 'menu-sign-out')}
              </>
            ) : (
              item(t('auth.signIn'), onSignIn, 'menu-sign-in')
            )}
          </div>
        </>
      )}
    </div>
  )
}
